// Platform super-admin API client. A SEPARATE client from lib/api.ts on purpose: it attaches the
// PLATFORM access token (never the tenant one), refreshes against /api/admin/auth/refresh, and on an
// unrecoverable 401 bounces to /admin/login (not /login). This is the client half of the two-session
// isolation — the server half is requirePlatformAdmin, which rejects a tenant token outright.
//
// Clean Architecture: the console (Presentation) talks ONLY to /api/admin/* (Logic). No DB, no secret.

import {
  getPlatformAccessToken,
  getPlatformRefreshToken,
  setPlatformTokens,
  clearPlatformSession,
  type PlatformAdmin,
} from './platformSession.js';

const BASE = '/api/admin';

// ─── DTOs (platform layer) — kept local to the web app; the console is frontend-only ────────────
export interface PlatformLoginResponse {
  challengeId: string;
  phoneHint: string;
  expiresInSec: number;
}
export interface PlatformSessionResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: string;
}
export interface PlatformVerifyResponse extends PlatformSessionResponse {
  admin: PlatformAdmin;
}
export interface PlatformMe {
  admin: { id: string; email: string; name: string | null; phoneHint: string | null; lastLoginAt: string | null };
}

export type CustomerStatus = 'active' | 'suspended' | 'cancelled';
export type Plan = 'pilot' | 'basic' | 'pro' | 'enterprise';

export interface CustomerUsage {
  numbers: number;
  campaigns30d: number;
  users: number;
}
export interface CustomerListItem {
  id: string;
  name: string;
  plan: string;
  status: CustomerStatus;
  createdAt: string;
  owner: { email: string; activated: boolean } | null;
  usage: CustomerUsage;
}
export interface CustomerUser {
  id: string;
  email: string;
  name: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}
export interface CustomerWaAccount {
  id: string;
  label: string;
  status: string;
  phoneNumber: string | null;
  healthScore: number | null;
}
export interface CustomerSubscription {
  id: string;
  status: string; // trialing | active | past_due | suspended | cancelled
  priceAgorot: number;
  currency: string;
  graceDays: number;
  nextInvoiceAt: string | null;
  currentPeriodEnd?: string | null;
}
export interface CustomerLastInvoice {
  id: string;
  status: string; // draft | issued | paid | overdue | failed | void
  amountAgorot: number;
  currency: string;
  issuedAt: string | null;
  dueAt: string | null;
  paidAt: string | null;
  morningDocumentUrl: string | null;
}
export interface CustomerDetail {
  id: string;
  name: string;
  plan: string;
  status: CustomerStatus;
  maxAccounts: number;
  maxTargets: number;
  seats: number;
  createdAt: string;
  users: CustomerUser[];
  waAccounts: CustomerWaAccount[];
  usage: CustomerUsage;
  pendingInvites: number;
  subscription: CustomerSubscription | null;
  lastInvoice: CustomerLastInvoice | null;
}
export interface CreateCustomerRequest {
  companyName: string;
  ownerEmail: string;
  ownerPhone: string;
  ownerName?: string;
  plan?: Plan;
  priceAgorot?: number;
  graceDays?: number;
  sendInviteSms?: boolean;
}
export interface CreateCustomerResponse {
  tenant: { id: string; name: string; plan: string; status: string };
  owner: { id: string; email: string };
  inviteUrl: string;
  inviteExpiresAt: string;
  inviteSmsSent: boolean;
}
export interface ResendInviteResponse {
  inviteUrl: string;
  inviteExpiresAt: string;
  inviteSmsSent: boolean;
}
/** PATCH /customers/:id returns the tenant's scalar fields + the (possibly updated) subscription. */
export interface CustomerPatchResult {
  id: string;
  name: string;
  plan: string;
  status: CustomerStatus;
  maxAccounts: number;
  maxTargets: number;
  seats: number;
  subscription: CustomerSubscription | null;
}

/** Attach the PLATFORM bearer token (when present). Never the tenant token. */
function withAuth(headers: HeadersInit): HeadersInit {
  const token = getPlatformAccessToken();
  return token ? { ...headers, authorization: `Bearer ${token}` } : headers;
}

// Single-flight refresh against the PLATFORM refresh endpoint.
let refreshing: Promise<boolean> | null = null;
async function tryRefresh(): Promise<boolean> {
  const refreshToken = getPlatformRefreshToken();
  if (!refreshToken) return false;
  if (!refreshing) {
    refreshing = fetch(`${BASE}/auth/refresh`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
      .then(async (r) => {
        if (!r.ok) return false;
        const s = (await r.json()) as PlatformSessionResponse;
        setPlatformTokens(s.accessToken, s.refreshToken);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
  }
  return refreshing;
}

async function raw(path: string, init?: RequestInit): Promise<Response> {
  return fetch(`${BASE}${path}`, {
    ...init,
    headers: withAuth({ 'content-type': 'application/json', ...(init?.headers ?? {}) }),
  });
}

async function req<T>(path: string, init?: RequestInit, _retried = false): Promise<T> {
  const res = await raw(path, init);
  if (res.status === 401 && !_retried && !path.startsWith('/auth/')) {
    if (await tryRefresh()) return req<T>(path, init, true);
    clearPlatformSession();
    if (typeof window !== 'undefined' && window.location.pathname !== '/admin/login') {
      window.location.assign('/admin/login');
    }
    throw new Error(`ADMIN API 401 ${path}`);
  }
  if (!res.ok) {
    let detail = '';
    try {
      detail = JSON.stringify(await res.json());
    } catch {
      /* non-JSON error body */
    }
    throw new Error(`ADMIN API ${res.status} ${path} ${detail}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export const adminApi = {
  // ─── platform auth (email → password → SMS OTP) ──────────────────────────────────────────────
  login: (email: string, password: string) =>
    req<PlatformLoginResponse>('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  verifyOtp: (challengeId: string, code: string) =>
    req<PlatformVerifyResponse>('/auth/verify-otp', { method: 'POST', body: JSON.stringify({ challengeId, code }) }),
  resendOtp: (challengeId: string) =>
    req<{ ok: boolean; phoneHint: string }>('/auth/otp/resend', { method: 'POST', body: JSON.stringify({ challengeId }) }),
  me: () => req<PlatformMe>('/auth/me'),
  logout: (refreshToken: string | null) =>
    req<void>('/auth/logout', { method: 'POST', body: JSON.stringify({ refreshToken }) }).catch(() => undefined),

  // ─── customers (all cross-tenant + audited server-side) ──────────────────────────────────────
  customers: () => req<{ customers: CustomerListItem[] }>('/customers').then((r) => r.customers),
  customer: (id: string) => req<{ customer: CustomerDetail }>(`/customers/${id}`).then((r) => r.customer),
  createCustomer: (body: CreateCustomerRequest) =>
    req<CreateCustomerResponse>('/customers', { method: 'POST', body: JSON.stringify(body) }),
  updateCustomer: (
    id: string,
    body: Partial<{ status: 'active' | 'suspended'; plan: Plan; name: string; priceAgorot: number; graceDays: number }>,
  ) => req<{ customer: CustomerPatchResult }>(`/customers/${id}`, { method: 'PATCH', body: JSON.stringify(body) }).then((r) => r.customer),
  resendInvite: (id: string, sendInviteSms: boolean) =>
    req<ResendInviteResponse>(`/customers/${id}/resend-invite`, { method: 'POST', body: JSON.stringify({ sendInviteSms }) }),
};
